import { useState, useEffect, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import { supabase } from '@/lib/supabase';

interface BankAccount {
  id: string;
  bank_name: string;
  account_number: string;
  account_holder: string;
}

interface WalletData {
  balance: number;
}

interface WithdrawHistory {
  id: string;
  amount: number;
  status: string;
  fee: number;
  rejection_reason: string | null;
  created_at: string;
  processed_at: string | null;
}

const statusStyle: Record<string, string> = {
  pending: 'bg-accent-500/15 text-accent-400',
  approved: 'bg-emerald-500/15 text-emerald-400',
  processing: 'bg-amber-500/15 text-amber-500',
  rejected: 'bg-rose-500/15 text-rose-400',
};

const statusLabel: Record<string, string> = {
  pending: 'Chờ xử lý',
  approved: 'Đã duyệt',
  processing: 'Đang xử lý',
  rejected: 'Từ chối',
};

const WITHDRAW_FEE = 3; // 3% phí rút tiền

export default function Withdraw() {
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [balance, setBalance] = useState(0);
  const [amount, setAmount] = useState('');
  const [withdrawPassword, setWithdrawPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [hasWithdrawPwd, setHasWithdrawPwd] = useState(false);
  const [history, setHistory] = useState<WithdrawHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData.session?.user.id;
        if (!userId) return;

        const [{ data: banksRaw }, { data: walletRaw }, { data: withdrawalsRaw }, { data: hasPwdRaw }] = await Promise.all([
          supabase.rpc('get_user_banks_json', { p_user_id: userId }),
          supabase.rpc('get_user_wallet_json', { p_user_id: userId }),
          supabase.rpc('get_user_withdraw_requests_json', { p_user_id: userId }),
          supabase.rpc('has_withdraw_password'),
        ]);

        const banks = Array.isArray(banksRaw) ? banksRaw : JSON.parse((banksRaw as string) || '[]');
        const wallets = Array.isArray(walletRaw) ? walletRaw : JSON.parse((walletRaw as string) || '[]');
        const withdrawals = Array.isArray(withdrawalsRaw) ? withdrawalsRaw : JSON.parse((withdrawalsRaw as string) || '[]');

        if (banks) {
          setBankAccounts(banks);
          if (banks.length > 0) setSelectedBankId(banks[0].id);
        }
        setBalance(Number(wallets[0]?.balance) || 0);
        setHasWithdrawPwd(Boolean(hasPwdRaw));
        setHistory(withdrawals.slice(0, 10) || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatAmount = (val: string) => {
    const num = val.replace(/[^0-9]/g, '');
    return num ? Number(num).toLocaleString('vi-VN') : '';
  };

  const numAmount = Number(amount.replace(/[^0-9]/g, '')) || 0;
  const fee = Math.round((numAmount * WITHDRAW_FEE) / 100);
  const receiveAmount = numAmount - fee;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedBankId) {
      setError('Vui lòng chọn tài khoản ngân hàng nhận tiền.');
      return;
    }
    if (numAmount < 50000) {
      setError('Số tiền rút tối thiểu là 50.000đ.');
      return;
    }
    if (numAmount > balance) {
      setError('Số dư khả dụng không đủ. Vui lòng kiểm tra lại.');
      return;
    }
    if (!hasWithdrawPwd) {
      setError('Bạn chưa đặt mật khẩu rút tiền. Vui lòng đặt tại mục Xác minh danh tính.');
      return;
    }
    if (!withdrawPassword) {
      setError('Vui lòng nhập mật khẩu rút tiền.');
      return;
    }

    setSubmitting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) throw new Error('Bạn chưa đăng nhập.');

      const { data: pwdOk, error: pwdError } = await supabase.rpc('verify_withdraw_password', {
        p_password: withdrawPassword,
      });
      if (pwdError) throw pwdError;
      if (!pwdOk) {
        setError('Mật khẩu rút tiền không đúng. Vui lòng kiểm tra lại.');
        return;
      }

      const { error: insertError } = await supabase.from('withdraw_requests').insert({
        user_id: userId,
        bank_account_id: selectedBankId,
        amount: numAmount,
        status: 'pending',
        fee,
      });

      if (insertError) throw insertError;

      setSuccess('Yêu cầu rút tiền đã được gửi. Vui lòng chờ admin duyệt.');
      setAmount('');
      setWithdrawPassword('');

      const { data: newHistoryRaw } = await supabase.rpc('get_user_withdraw_requests_json', { p_user_id: userId });
      const newHistory = Array.isArray(newHistoryRaw) ? newHistoryRaw : JSON.parse((newHistoryRaw as string) || '[]');
      setHistory(newHistory.slice(0, 10) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gửi yêu cầu thất bại.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500';
  const labelCls = 'block text-xs font-semibold text-foreground-600 mb-1.5';

  return (
    <AccountLayout>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-foreground-950 mb-6">Rút tiền về ngân hàng</h1>

        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
            <i className="ri-error-warning-line text-rose-400 text-base"></i>
            <p className="text-sm text-rose-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-3 flex items-center gap-2">
            <i className="ri-check-line text-emerald-400 text-base"></i>
            <p className="text-sm text-emerald-400">{success}</p>
          </div>
        )}

        <div className="rounded-xl border border-background-200/60 bg-background-100 p-5 mb-6">
          <div className="flex items-center gap-2 text-foreground-600 text-xs mb-1">
            <i className="ri-wallet-3-line text-base"></i> Số dư khả dụng
          </div>
          <p className="text-2xl font-bold text-foreground-950 whitespace-nowrap">
            {loading ? '...' : `${balance.toLocaleString('vi-VN')}đ`}
          </p>
        </div>

        {bankAccounts.length === 0 ? (
          <div className="rounded-xl border border-accent-500/30 bg-accent-500/10 p-5 mb-6 flex items-start gap-3">
            <i className="ri-bank-line text-accent-400 text-xl mt-0.5"></i>
            <div>
              <p className="text-sm font-semibold text-foreground-950">Bạn chưa liên kết tài khoản ngân hàng</p>
              <p className="text-sm text-foreground-600 mt-1">Vui lòng liên kết ngân hàng trước khi rút tiền.</p>
              <Link to="/ngan-hang" className="inline-block mt-2 text-sm font-semibold text-accent-400 hover:text-accent-300 cursor-pointer whitespace-nowrap">
                Liên kết ngân hàng ngay →
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="rounded-xl border border-background-200/60 bg-background-100 p-5 mb-6 space-y-4">
            <h3 className="text-sm font-semibold text-foreground-950">Thông tin rút tiền</h3>

            <div>
              <label className={labelCls}>Ngân hàng nhận tiền</label>
              <select value={selectedBankId} onChange={(e) => setSelectedBankId(e.target.value)} className={inputCls}>
                {bankAccounts.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.bank_name} - {b.account_number} - {b.account_holder}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelCls}>Số tiền rút (VNĐ)</label>
              <input
                type="text"
                value={amount}
                onChange={(e) => setAmount(formatAmount(e.target.value))}
                placeholder="VD: 1.000.000"
                className={inputCls}
              />
              <div className="mt-2 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-foreground-600">Phí rút ({WITHDRAW_FEE}%)</span>
                  <span className="text-foreground-900">-{fee.toLocaleString('vi-VN')}đ</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span className="text-foreground-700">Số tiền nhận được</span>
                  <span className="text-emerald-500">{receiveAmount.toLocaleString('vi-VN')}đ</span>
                </div>
              </div>
            </div>

            <div>
              <label className={labelCls}>Mật khẩu rút tiền</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={withdrawPassword}
                  onChange={(e) => setWithdrawPassword(e.target.value)}
                  placeholder="Nhập mật khẩu rút tiền"
                  className={inputCls + ' pr-10'}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 hover:text-foreground-900 cursor-pointer"
                  aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                </button>
              </div>
              {!hasWithdrawPwd && (
                <p className="mt-1.5 text-xs text-amber-500 flex items-center gap-1">
                  <i className="ri-information-line"></i> Bạn chưa đặt mật khẩu rút tiền.
                  <Link to="/xac-minh-danh-tinh" className="font-semibold underline hover:opacity-80 whitespace-nowrap">
                    Đặt ngay
                  </Link>
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
            >
              {submitting ? 'Đang gửi...' : 'Gửi yêu cầu rút tiền'}
            </button>
          </form>
        )}

        <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
          <h3 className="text-sm font-semibold text-foreground-950 mb-4">Lịch sử rút tiền</h3>
          {loading ? (
            <p className="text-sm text-foreground-600">Đang tải...</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-foreground-600">Chưa có yêu cầu rút tiền nào</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[500px]">
                <thead>
                  <tr className="text-left">
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-foreground-600">Số tiền</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-foreground-600">Phí</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-foreground-600">Trạng thái</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-foreground-600">Thời gian</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-foreground-600">Đã chuyển khoản lúc</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t border-background-200/40">
                      <td className="px-3 py-2 font-semibold text-foreground-950 whitespace-nowrap">
                        {Number(h.amount).toLocaleString('vi-VN')}đ
                      </td>
                      <td className="px-3 py-2 text-foreground-700 whitespace-nowrap">
                        {Number(h.fee).toLocaleString('vi-VN')}đ
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${statusStyle[h.status]}`}>
                          {statusLabel[h.status]}
                        </span>
                        {h.status === 'rejected' && h.rejection_reason && (
                          <p className="text-xs text-rose-400 mt-1">{h.rejection_reason}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-foreground-600 whitespace-nowrap">
                        {new Date(h.created_at).toLocaleString('vi-VN')}
                      </td>
                      <td className="px-3 py-2 text-foreground-600 whitespace-nowrap">
                        {h.processed_at ? new Date(h.processed_at).toLocaleString('vi-VN') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AccountLayout>
  );
}